import axios from "axios";
import type {
  SessionSummary,
  SessionDetail,
  ExamSummary,
  ExamDetail,
  SessionGrades,
  SessionAnalysis,
  CostBySessionModelRow,
  LlmSettings,
  LlmProvider,
  ClassSummary,
  StudentSummary,
  SubscriberSummary,
  SubscribersResponse,
  CreditWallet,
  CreditTransaction,
  ReadCreditConfig,
  CreditPackage,
  Payment,
  OrgMember,
  UserProfile,
  AdminTestRunResult,
  AdminTestRecord,
  AdminTestHistoryResponse,
  AdminTestModelsResponse,
  AdminTestPromptsResponse,
  AdminTestQualityRating,
  RegradeResult,
  ExamCSummary,
  ExamCDetail,
  StartGradingCResponse,
  ExamDSummary,
  ExamDDetail,
  StartGradingDResponse,
} from "./types";

const api = axios.create({
  baseURL: "/",
  withCredentials: true,
  headers: {
    "X-Requested-With": "XMLHttpRequest"
  }
});

export async function fetchExams(): Promise<{ exams: ExamSummary[]; total_cost: number }> {
  const { data } = await api.get("/api/exams");
  return {
    exams: data.exams || data.sessions || [],
    total_cost: data.total_cost || 0,
  };
}

export async function createExam(formData: FormData): Promise<{ exam_id: number }> {
  const { data } = await api.post("/api/exams", formData, {
    headers: { "Content-Type": "multipart/form-data" }
  });
  return { exam_id: data.exam_id || data.session_id };
}

export async function fetchExamDetail(examId: string | number): Promise<ExamDetail> {
  const { data } = await api.get(`/api/exams/${examId}`);
  return {
    id: data.id,
    session_name: data.session_name || data.exam_name,
    exam_name: data.exam_name || data.session_name,
    test_date: data.test_date,
    description: data.description,
    subscriber_id: data.subscriber_id,
    class_id: data.class_id,
    grading_mode: data.grading_mode,
    session_cost: data.session_cost ?? data.exam_cost,
    rubric_file_id: data.rubric_file_id,
    rubric_preview: data.rubric_preview || null,
    feedback_language: data.feedback_language,
    max_points: data.max_points,
    latest_run: data.latest_run,
    runs: data.runs || [],
    questions: (data.questions || []).map((q: Record<string, unknown>) => ({
      ...q,
      max_points: (q.maximum_points ?? q.max_points) as number | null,
    })),
    papers: data.papers || data.submissions || [],
    submissions: data.submissions || data.papers || [],
    questions_approved: data.questions_approved ?? false,
  };
}

export async function fetchSessions(): Promise<{ sessions: SessionSummary[]; total_cost: number }> {
  const data = await fetchExams();
  return { sessions: data.exams, total_cost: data.total_cost };
}

export async function createSession(formData: FormData): Promise<{ session_id: number }> {
  const data = await createExam(formData);
  return { session_id: data.exam_id };
}

export async function fetchSessionDetail(sessionId: string | number): Promise<SessionDetail> {
  return fetchExamDetail(sessionId);
}

export async function deleteExam(examId: number) {
  const { data } = await api.delete(`/api/exams/${examId}`);
  return data;
}

export async function deleteSession(sessionId: number) {
  return deleteExam(sessionId);
}

export async function updateExamQuestions(
  sessionId: number | string,
  questions: import("./types").QuestionData[]
): Promise<import("./types").QuestionData[]> {
  const { data } = await api.patch(`/api/sessions/${sessionId}/questions`, { questions });
  return (data as { questions: import("./types").QuestionData[] }).questions;
}

export async function approveQuestions(examId: number): Promise<{ questions_approved: boolean }> {
  const { data } = await api.patch(`/api/exams/${examId}/approve-questions`);
  return data as { questions_approved: boolean };
}

export async function unapproveQuestions(examId: number): Promise<{ questions_approved: boolean }> {
  const { data } = await api.patch(`/api/exams/${examId}/unapprove-questions`);
  return data as { questions_approved: boolean };
}

export async function startGrading(sessionId: number, strictness: string, feedbackLanguage?: string) {
  const form = new FormData();
  form.append("strictness", strictness);
  if (feedbackLanguage) form.append("feedback_language", feedbackLanguage);
  const { data } = await api.post(`/api/exams/${sessionId}/runs`, form);
  return data as { grading_id: number; total_papers: number };
}

export async function fetchCurrentUser(): Promise<UserProfile> {
  const { data } = await api.get("/api/me");
  return data as UserProfile;
}

export async function updateCurrentUser(updates: Partial<Pick<UserProfile, "full_name" | "feedback_language" | "ui_language">>): Promise<UserProfile> {
  const { data } = await api.patch("/api/me", updates);
  return data as UserProfile;
}

export async function pollGrading(gradingId: number) {
  const { data } = await api.get(`/grading/grading_progress/${gradingId}`);
  return data;
}

export async function cancelGrading(gradingId: number) {
  const { data } = await api.post(`/grading/cancel_grading/${gradingId}`);
  return data;
}

export async function deleteRubric(sessionId: number) {
  const { data } = await api.delete(`/api/exams/${sessionId}/rubric`);
  return data;
}

export async function deleteGradingRun(sessionId: number, runId: number) {
  const { data } = await api.delete(`/api/exams/${sessionId}/runs/${runId}`);
  return data;
}

export async function deleteAllGradingRuns(sessionId: number) {
  const { data } = await api.delete(`/api/exams/${sessionId}/runs`);
  return data;
}

export async function regradeQuestions(
  paperId: number,
  questionDisplays: string[]
): Promise<RegradeResult> {
  const { data } = await api.post(`/grading/papers/${paperId}/regrade`, {
    question_displays: questionDisplays,
  });
  return data;
}

export async function fetchExamGrades(examId: number | string, runId?: number | null): Promise<SessionGrades> {
  const { data } = await api.get(`/api/exams/${examId}/grades`, {
    params: runId ? { run_id: runId } : {}
  });
  return data.grades;
}

export async function fetchExamAnalysis(examId: number | string, runId?: number | null): Promise<SessionAnalysis> {
  const { data } = await api.get(`/api/exams/${examId}/analysis`, {
    params: runId ? { run_id: runId } : {}
  });
  return data;
}

export async function fetchSessionGrades(sessionId: number | string, runId?: number | null): Promise<SessionGrades> {
  return fetchExamGrades(sessionId, runId);
}

export async function fetchSessionAnalysis(sessionId: number | string, runId?: number | null): Promise<SessionAnalysis> {
  return fetchExamAnalysis(sessionId, runId);
}

export async function fetchCostsByExamModel(): Promise<{ total_cost: number; rows: CostBySessionModelRow[] }> {
  const { data } = await api.get(`/api/costs/by_exam_model`);
  return data;
}

/** @deprecated use fetchCostsByExamModel */
export async function fetchCostsBySessionModel(): Promise<{ total_cost: number; rows: CostBySessionModelRow[] }> {
  return fetchCostsByExamModel();
}

export async function fetchLlmSettings(): Promise<LlmSettings> {
  const { data } = await api.get(`/api/settings/llm`);
  return data;
}

export async function updateLlmSettings(defaultProvider: LlmProvider, model: string): Promise<LlmSettings> {
  const { data } = await api.patch(`/api/settings/llm`, {
    default_provider: defaultProvider,
    model,
  });
  return data;
}

export async function updateModDLlmSettings(payload: {
  extraction_provider: LlmProvider;
  extraction_model: string;
  grading_provider: LlmProvider;
  grading_model: string;
}): Promise<LlmSettings> {
  const { data } = await api.patch(`/api/settings/llm/mod-d`, payload);
  return data;
}

export async function upsertLlmModel(payload: {
  provider: LlmProvider;
  model: string;
  input_per_1m: number;
  output_per_1m: number;
}): Promise<LlmSettings> {
  const { data } = await api.post(`/api/settings/llm/models`, payload);
  return data;
}

export async function deleteLlmModel(provider: LlmProvider, model: string): Promise<LlmSettings> {
  const { data } = await api.delete(`/api/settings/llm/models`, {
    data: { provider, model },
  });
  return data;
}

export async function fetchClasses(): Promise<ClassSummary[]> {
  const { data } = await api.get(`/api/classes`);
  return data.classes || [];
}

export async function createClass(name: string): Promise<ClassSummary> {
  const { data } = await api.post(`/api/classes`, { name });
  return data;
}

export async function fetchClassStudents(classId: number): Promise<StudentSummary[]> {
  const { data } = await api.get(`/api/classes/${classId}/students`);
  return data.students || [];
}

export async function createClassStudent(classId: number, payload: { full_name: string; student_no: string }): Promise<StudentSummary> {
  const { data } = await api.post(`/api/classes/${classId}/students`, payload);
  return data;
}

export async function updateStudent(studentId: number, payload: { full_name: string; student_no: string }): Promise<StudentSummary> {
  const { data } = await api.patch(`/api/students/${studentId}`, payload);
  return data;
}

export async function uploadExamSubmission(examId: number, studentId: number, file: File) {
  const form = new FormData();
  form.append("student_id", String(studentId));
  form.append("file", file);
  const { data } = await api.post(`/api/exams/${examId}/submissions`, form, {
    headers: { "Content-Type": "multipart/form-data" }
  });
  return data;
}

export async function fetchSubscribers(): Promise<SubscribersResponse> {
  const { data } = await api.get(`/api/subscribers`);
  return {
    active_subscriber_id: data.active_subscriber_id,
    subscribers: data.subscribers || [],
    user_role: data.user_role,
  };
}

export async function createSubscriber(payload: { name: string; subscriber_type: "INDIVIDUAL" | "INSTITUTION" }) {
  const { data } = await api.post(`/api/subscribers`, payload);
  return data;
}

export async function activateSubscriber(subscriberId: number) {
  const { data } = await api.post(`/api/subscribers/activate`, { subscriber_id: subscriberId });
  return data;
}

export async function fetchMembers(orgId: number): Promise<{ subscriber_id: number; members: OrgMember[] }> {
  const { data } = await api.get(`/api/subscribers/${orgId}/members`);
  return { subscriber_id: data.subscriber_id, members: data.members || [] };
}

export async function addMember(orgId: number, payload: { full_name: string; email: string; role?: "TEACHER" }): Promise<OrgMember> {
  const { data } = await api.post(`/api/subscribers/${orgId}/members`, payload);
  return data;
}

export async function removeMember(orgId: number, userId: number) {
  const { data } = await api.delete(`/api/subscribers/${orgId}/members/${userId}`);
  return data as { message: string; user_id: number };
}

export async function updateMemberRole(orgId: number, userId: number, role: string): Promise<OrgMember> {
  const { data } = await api.patch(`/api/subscribers/${orgId}/members/${userId}`, { role });
  return data as OrgMember;
}

export async function assignClassTeacher(classId: number, userId: number) {
  const { data } = await api.post(`/api/classes/${classId}/teachers`, { user_id: userId });
  return data as { message: string; class_id: number; user_id: number };
}

export async function unassignClassTeacher(classId: number, userId: number) {
  const { data } = await api.delete(`/api/classes/${classId}/teachers/${userId}`);
  return data as { message: string; class_id: number; user_id: number };
}

export async function fetchWallet(): Promise<CreditWallet> {
  const { data } = await api.get(`/api/credits/wallet`);
  return data as CreditWallet;
}

export async function fetchCreditTransactions(): Promise<{ subscriber_id: number; transactions: CreditTransaction[] }> {
  const { data } = await api.get(`/api/credits/transactions`);
  return data;
}

export async function purchaseMockCredits(amount: number): Promise<{ read_balance: number; payment_id: number }> {
  const { data } = await api.post(`/api/credits/purchase_mock`, { amount });
  return data;
}

// --- Billing / payments (provider-agnostic) ---

/** List active credit packages available for purchase. */
export async function fetchCreditPackages(): Promise<{ packages: CreditPackage[] }> {
  const { data } = await api.get(`/api/billing/packages`);
  return data;
}

/** Start a hosted checkout for a package. Returns the provider redirect URL. */
export async function createCheckout(packageId: number): Promise<{ checkout_url: string; payment_id: number }> {
  const { data } = await api.post(`/api/billing/checkout`, { package_id: packageId });
  return data;
}

/** Payment history for the active organization. */
export async function fetchPayments(): Promise<{ subscriber_id: number; payments: Payment[] }> {
  const { data } = await api.get(`/api/billing/payments`);
  return data;
}

/** Admin-only: list all credit packages (including inactive). */
export async function fetchAllCreditPackages(): Promise<{ packages: CreditPackage[] }> {
  const { data } = await api.get(`/api/admin/billing/packages`);
  return data;
}

/** Admin-only: create a credit package. */
export async function createCreditPackage(
  pkg: Pick<CreditPackage, 'name' | 'reads' | 'price'> & Partial<CreditPackage>,
): Promise<CreditPackage> {
  const { data } = await api.post(`/api/admin/billing/packages`, pkg);
  return data as CreditPackage;
}

/** Admin-only: update a credit package. */
export async function updateCreditPackage(
  packageId: number,
  patch: Partial<CreditPackage>,
): Promise<CreditPackage> {
  const { data } = await api.put(`/api/admin/billing/packages/${packageId}`, patch);
  return data as CreditPackage;
}

/** Admin-only: adjust credits for any subscriber. amount_usd is converted server-side using READS_PER_USD rate. */
export async function adminAdjustCredits(
  subId: number,
  amount_usd: number,
  note?: string,
): Promise<{ read_balance: number; reads_added: number; amount_usd: number }> {
  const { data } = await api.post(`/api/admin/subscribers/${subId}/credits`, { amount_usd, note });
  return data;
}

/** Fetch wallet balance for a specific subscriber (admin). */
export async function fetchSubWallet(subId: number): Promise<CreditWallet> {
  const { data } = await api.get(`/api/admin/subscribers/${subId}/wallet`);
  return data as CreditWallet;
}

/** Fetch AI usage records for current subscriber. */
export async function fetchUsageRecords(): Promise<{ subscriber_id: number; records: CreditTransaction[] }> {
  const { data } = await api.get(`/api/credits/usage`);
  return data;
}

/** Admin-only: fetch read-credit pricing configuration. */
export async function fetchReadCreditConfig(): Promise<ReadCreditConfig> {
  const { data } = await api.get(`/api/admin/read-credit-config`);
  return data as ReadCreditConfig;
}

/** Admin-only: update read-credit pricing configuration. */
export async function updateReadCreditConfig(cfg: Partial<ReadCreditConfig>): Promise<ReadCreditConfig> {
  const { data } = await api.put(`/api/admin/read-credit-config`, cfg);
  return data as ReadCreditConfig;
}

/** Admin-only: aggregated credit usage stats, optionally filtered by created_at range. */
export async function fetchAdminStats(start_date?: string, end_date?: string): Promise<import('./types').AdminStats> {
  const params: Record<string, string> = {};
  if (start_date) params.start_date = start_date;
  if (end_date) params.end_date = end_date;
  const { data } = await api.get('/api/admin/stats', { params });
  return data;
}

// --- Dev user switcher ---

export interface DevUser {
  id: string;
  email: string;
  full_name: string | null;
  user_role: string;
  membership_role: string | null;
}

export async function fetchDevUsers(): Promise<DevUser[]> {
  const { data } = await api.get('/api/dev/users');
  return data as DevUser[];
}

export async function activateDevUser(email: string): Promise<void> {
  await api.post('/api/dev/users/activate', { email });
}

// ── Admin GenAI Testing ──────────────────────────────────────────────────

export async function fetchAdminTestPrompts(): Promise<AdminTestPromptsResponse> {
  const { data } = await api.get('/api/admin/test/prompts');
  return data;
}

export async function fetchAdminTestModels(): Promise<AdminTestModelsResponse> {
  const { data } = await api.get('/api/admin/test/models');
  return data;
}

export async function runAdminTestRubric(formData: FormData): Promise<AdminTestRunResult> {
  const { data } = await api.post('/api/admin/test/rubric', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: 120000,
  });
  return data;
}

export async function runAdminTestQuestions(formData: FormData): Promise<AdminTestRunResult> {
  const { data } = await api.post('/api/admin/test/questions', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: 120000,
  });
  return data;
}

export async function runAdminTestGrading(formData: FormData): Promise<AdminTestRunResult> {
  const { data } = await api.post('/api/admin/test/grading', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: 180000,
  });
  return data;
}

export async function updateAdminTestRating(
  recordId: number,
  quality_rating: AdminTestQualityRating | null,
  notes?: string
): Promise<AdminTestRecord> {
  const { data } = await api.patch(`/api/admin/test/${recordId}/rating`, {
    quality_rating,
    notes,
  });
  return data;
}

export async function fetchAdminTestHistory(params?: {
  test_type?: string;
  quality_rating?: string;
  limit?: number;
  offset?: number;
}): Promise<AdminTestHistoryResponse> {
  const { data } = await api.get('/api/admin/test/history', { params });
  return data;
}

export async function fetchAdminTestRecord(recordId: number): Promise<AdminTestRecord> {
  const { data } = await api.get(`/api/admin/test/${recordId}`);
  return data;
}

// ── Mod C Exam API ───────────────────────────────────────────────────────

export async function createExamC(formData: FormData): Promise<{ exam_id: number }> {
  const { data } = await api.post("/api/exams-c", formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return { exam_id: data.exam_id };
}

export async function fetchExamsCList(): Promise<{ exams: ExamCSummary[] }> {
  const { data } = await api.get("/api/exams-c");
  return { exams: data.exams || [] };
}

export async function fetchExamCDetail(examId: number | string): Promise<ExamCDetail> {
  const { data } = await api.get(`/api/exams-c/${examId}`);
  return data;
}

export async function uploadExamCSubmission(
  examId: number,
  studentId: number,
  pageFiles: File[]
): Promise<{ submission_id: number; file_ids: string[] }> {
  const formData = new FormData();
  formData.append("student_id", String(studentId));
  pageFiles.forEach((f) => formData.append("pages[]", f));
  const { data } = await api.post(`/api/exams-c/${examId}/submissions`, formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return data;
}

export async function deleteExamCSubmission(examId: number, subId: number): Promise<void> {
  await api.delete(`/api/exams-c/${examId}/submissions/${subId}`);
}

export async function startExamCGrading(
  examId: number,
  strictness: string,
  feedbackLanguage?: string
): Promise<StartGradingCResponse> {
  const { data } = await api.post(`/api/exams-c/${examId}/grade`, {
    strictness,
    feedback_language: feedbackLanguage,
  });
  return data;
}

// ── Mod D Exam API ───────────────────────────────────────────────────────

export async function createExamD(formData: FormData): Promise<{ exam_id: number }> {
  const { data } = await api.post("/api/exams-d", formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return { exam_id: data.exam_id };
}

export async function fetchExamsDList(): Promise<{ exams: ExamDSummary[] }> {
  const { data } = await api.get("/api/exams-d");
  return { exams: data.exams || [] };
}

export async function fetchExamDDetail(examId: number | string): Promise<ExamDDetail> {
  const { data } = await api.get(`/api/exams-d/${examId}`);
  return data;
}

export async function uploadExamDSubmission(
  examId: number,
  studentId: number,
  pageFiles: File[]
): Promise<{ submission_id: number; file_ids: string[] }> {
  const formData = new FormData();
  formData.append("student_id", String(studentId));
  pageFiles.forEach((f) => formData.append("pages[]", f));
  const { data } = await api.post(`/api/exams-d/${examId}/submissions`, formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return data;
}

export async function deleteExamDSubmission(examId: number, subId: number): Promise<void> {
  await api.delete(`/api/exams-d/${examId}/submissions/${subId}`);
}

export async function startExamDGrading(
  examId: number,
  strictness: string,
  feedbackLanguage?: string
): Promise<StartGradingDResponse> {
  const { data } = await api.post(`/api/exams-d/${examId}/grade`, {
    strictness,
    feedback_language: feedbackLanguage,
  });
  return data;
}

export async function updateExamDQuestions(
  examId: number,
  questions: import("./types").QuestionData[]
): Promise<import("./types").QuestionData[]> {
  const { data } = await api.patch(`/api/exams-d/${examId}/questions`, { questions });
  return (data as { questions: import("./types").QuestionData[] }).questions;
}

export async function approveExamDQuestions(examId: number): Promise<void> {
  await api.patch(`/api/exams-d/${examId}/approve-questions`);
}

export async function unapproveExamDQuestions(examId: number): Promise<void> {
  await api.patch(`/api/exams-d/${examId}/unapprove-questions`);
}

export async function deleteExamC(examId: number): Promise<void> {
  await api.delete(`/api/exams-c/${examId}`);
}

export async function deleteExamD(examId: number): Promise<void> {
  await api.delete(`/api/exams-d/${examId}`);
}

// ── Auth ──────────────────────────────────────────────────────────────────────

export interface AuthUser {
  id: string
  email: string
  full_name: string | null
  profile_picture: string | null
  user_role: string
  membership_role: string | null
  feedback_language: string
  auth_provider: string | null
  email_verified: boolean
}

export interface AuthStatus {
  authenticated: boolean
  user: AuthUser | null
}

export async function checkAuthStatus(): Promise<AuthStatus> {
  const { data } = await api.get<AuthStatus>('/api/auth/status')
  return data
}

export async function checkEmail(email: string): Promise<{ exists: boolean; has_password?: boolean; has_google?: boolean }> {
  const { data } = await api.post('/api/auth/check-email', { email })
  return data
}

export async function loginWithPassword(email: string, password: string): Promise<AuthUser> {
  const { data } = await api.post<{ ok: boolean; user: AuthUser }>('/api/auth/login', { email, password })
  return data.user
}

export async function registerWithPassword(email: string, password: string): Promise<AuthUser> {
  const { data } = await api.post<{ ok: boolean; user: AuthUser }>('/api/auth/register', { email, password })
  return data.user
}

export async function loginWithGoogle(credential: string): Promise<AuthUser> {
  const { data } = await api.post<{ ok: boolean; user: AuthUser }>('/api/auth/google', { credential })
  return data.user
}

export async function logout(): Promise<void> {
  await api.post('/api/auth/logout')
}

export default api;
